/**
 * Phase 307 — useGeofencePing
 *
 * Silently pings POST /api/staff/attendance/ping every 5 minutes while
 * the coach PWA is open and a staff role is detected. Exposes:
 *   { statusLabel: 'clocked_in'|'clocked_out'|'unknown',
 *     lastResult, lastError, manualPing() }
 *
 * Design notes:
 *  • Graceful degradation: if navigator.geolocation is missing
 *    (rare on PWAs but possible on desktop browsers), returns
 *    statusLabel='unknown' and never throws.
 *  • Respects the feature flag — if the ping endpoint 403s with
 *    FEATURE_DISABLED, we silently stop polling for the session
 *    (no scary toasts at the owner).
 *  • Visibility-aware: stops the polling when the tab is hidden to
 *    save battery. Resumes on focus.
 *  • One-shot manualPing() is exported so a UI button can force a
 *    refresh on demand.
 *
 * Intended consumer: any staff-facing page (CoachDashboard, etc.)
 * that wants the StatusDot rendered + the ping running in the
 * background.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';

const POLL_INTERVAL_MS = 5 * 60 * 1000;          // 5 minutes
const GEOLOCATION_TIMEOUT_MS = 15_000;
const MAXIMUM_AGE_MS = 60_000;

const STAFF_ROLES = new Set(['coach', 'employee', 'manager', 'academy_owner']);

function _getStoredRole() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    return (u?.role || '').toLowerCase();
  } catch (_) {
    return null;
  }
}

function _isStaffRole(role) {
  return STAFF_ROLES.has((role || '').toLowerCase());
}

async function _getCurrentPositionAsync() {
  return new Promise((resolve, reject) => {
    if (!navigator?.geolocation?.getCurrentPosition) {
      reject(new Error('geolocation_unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      (err) => reject(err),
      {
        enableHighAccuracy: true,
        timeout: GEOLOCATION_TIMEOUT_MS,
        maximumAge: MAXIMUM_AGE_MS,
      },
    );
  });
}

export default function useGeofencePing({ role = null, enabled = true } = {}) {
  const [statusLabel, setStatusLabel] = useState('unknown');
  const [lastResult, setLastResult] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [featureDisabled, setFeatureDisabled] = useState(false);

  const intervalRef = useRef(null);
  const inflightRef = useRef(false);
  const effectiveRole = role || _getStoredRole();
  const eligible = enabled && _isStaffRole(effectiveRole) && !featureDisabled;

  // Single-ping routine — used by both poll + manualPing.
  const doPing = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    setLastError(null);
    try {
      const pos = await _getCurrentPositionAsync();
      const { latitude, longitude, accuracy } = pos.coords || {};
      const { data } = await api.post('/staff/attendance/ping', {
        lat: latitude,
        lng: longitude,
        accuracy_meters: accuracy ?? null,
      });
      setLastResult(data);
      setStatusLabel(data?.status_label || 'unknown');
    } catch (err) {
      const code = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (code === 403 && (detail?.code === 'FEATURE_DISABLED' || detail === 'No academy assigned')) {
        // Stop polling for this session — feature not enabled / no academy.
        setFeatureDisabled(true);
        setStatusLabel('unknown');
      } else if (err?.code === 1 /* PERMISSION_DENIED */) {
        // The browser denied geolocation. Surface a clear error but
        // don't keep retrying — the user will need to grant permission.
        setLastError('geolocation_denied');
        setFeatureDisabled(true);
      } else if (err?.message === 'geolocation_unavailable') {
        setLastError('geolocation_unavailable');
        setFeatureDisabled(true);
      } else {
        setLastError(err?.message || 'ping_failed');
      }
    } finally {
      inflightRef.current = false;
    }
  }, []);

  // Mount: hydrate the badge with the current shift state ASAP,
  // then kick off the polling loop.
  useEffect(() => {
    if (!eligible) {
      setStatusLabel('unknown');
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/staff/attendance/me');
        if (!cancelled) setStatusLabel(data?.status_label || 'clocked_out');
      } catch (err) {
        const code = err?.response?.status;
        if (code === 403) {
          setFeatureDisabled(true);
        }
      }
    })();

    // Fire the FIRST ping ~10 seconds after mount so we don't hammer
    // the browser geolocation prompt the instant the dashboard loads.
    const initialKick = setTimeout(() => doPing(), 10_000);
    intervalRef.current = setInterval(() => doPing(), POLL_INTERVAL_MS);

    // Pause on tab-hide to save battery.
    const onVisibility = () => {
      if (document.hidden) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else if (!intervalRef.current && !featureDisabled) {
        // Refresh + restart on tab refocus.
        doPing();
        intervalRef.current = setInterval(() => doPing(), POLL_INTERVAL_MS);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearTimeout(initialKick);
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [eligible, doPing, featureDisabled]);

  return {
    statusLabel,
    lastResult,
    lastError,
    featureDisabled,
    eligible,
    manualPing: doPing,
  };
}

/**
 * Drop-in pill component that visualises the live geofence status.
 * Renders nothing for non-staff or when the feature isn't enabled.
 */
export function GeofenceStatusDot({ statusLabel, featureDisabled, eligible }) {
  if (!eligible || featureDisabled) return null;
  const palette = statusLabel === 'clocked_in'
    ? { dot: 'bg-emerald-500', ring: 'bg-emerald-400/30', text: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Clocked in' }
    : statusLabel === 'clocked_out'
      ? { dot: 'bg-slate-400', ring: 'bg-slate-300/30', text: 'text-slate-600 bg-slate-50 border-slate-200', label: 'Off duty' }
      : { dot: 'bg-amber-500', ring: 'bg-amber-300/30', text: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Status unknown' };

  return (
    <div
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs font-medium ${palette.text}`}
      data-testid="geofence-status-dot"
      title={`Geofence: ${palette.label}`}
    >
      <span className="relative flex h-2 w-2">
        {statusLabel === 'clocked_in' && (
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${palette.ring} opacity-75`} />
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${palette.dot}`} />
      </span>
      <span>{palette.label}</span>
    </div>
  );
}
